import { useState } from "react";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import {
	useCreateAnnouncementMutation,
	useDeleteAnnouncementMutation,
	useListAnnouncementsQuery,
	useUpdateAnnouncementMutation,
	type Announcement,
} from "../../store/api/announcementsApi";
import AnnouncementFilters from "./components/AnnouncementFilters";
import AnnouncementTable from "./components/AnnouncementTable";
import CreateAnnouncementModal from "./components/CreateAnnouncementModal";
import EditAnnouncementModal from "./components/EditAnnouncementModal";

const AnnouncementManagement = () => {
	const [page, setPage] = useState(1);
	const [filters, setFilters] = useState({
		status: "",
		priority: "",
	});
	const [selectedAnnouncement, setSelectedAnnouncement] =
		useState<Announcement | null>(null);
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);

	const {
		data: announcementsData,
		isLoading,
		error,
		refetch,
	} = useListAnnouncementsQuery({
		page,
		page_size: 20,
		status: filters.status || undefined,
		priority: filters.priority || undefined,
		ordering: "-created_at",
	});

	const [createAnnouncement, { isLoading: isCreating }] =
		useCreateAnnouncementMutation();
	const [updateAnnouncement, { isLoading: isUpdating }] =
		useUpdateAnnouncementMutation();
	const [deleteAnnouncement, { isLoading: isDeleting }] =
		useDeleteAnnouncementMutation();

	const handleFiltersChange = (newFilters: any) => {
		setFilters((prev) => ({
			...prev,
			...newFilters,
		}));
		setPage(1); // Reset to first page when filters change
	};

	const handleClearFilters = () => {
		setFilters({
			status: "",
			priority: "",
		});
		setPage(1);
	};

	const handleEditAnnouncement = (announcement: Announcement) => {
		setSelectedAnnouncement(announcement);
		setIsEditModalOpen(true);
	};

	const handleDeleteAnnouncement = async (announcementId: string) => {
		if (
			!window.confirm(
				"Are you sure you want to delete this announcement? This action cannot be undone.",
			)
		) {
			return;
		}

		try {
			await deleteAnnouncement(announcementId).unwrap();
			refetch();
		} catch (error) {
			console.error("Failed to delete announcement:", error);
			alert("Failed to delete announcement. Please try again.");
		}
	};

	const handleCreateSuccess = () => {
		setIsCreateModalOpen(false);
		refetch();
	};

	const handleUpdateSuccess = () => {
		setIsEditModalOpen(false);
		setSelectedAnnouncement(null);
		refetch();
	};

	if (error) {
		return (
			<div className="w-full flex flex-col items-center justify-center min-h-screen space-y-4">
				<Icon name="AlertCircle" size={48} className="text-destructive" />
				<h2 className="text-xl font-semibold text-foreground">
					Error Loading Announcements
				</h2>
				<p className="text-muted-foreground">
					Failed to load announcements. Please try again.
				</p>
				<Button onClick={() => refetch()}>Retry</Button>
			</div>
		);
	}

	const announcements = announcementsData?.results || [];

	return (
		<div className="w-full">
			<div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
				{/* Page Header */}
				<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 sm:mb-6">
					<div>
						<h1 className="text-2xl sm:text-3xl font-bold text-foreground">
							Announcement Management
						</h1>
						<p className="text-muted-foreground mt-1">
							Create and manage system-wide announcements for tenants and users
						</p>
					</div>

					<Button
						onClick={() => setIsCreateModalOpen(true)}
						iconName="Plus"
						iconPosition="left"
					>
						Create Announcement
					</Button>
				</div>

				{/* Filters */}
				<AnnouncementFilters
					onFiltersChange={handleFiltersChange}
					onClearFilters={handleClearFilters}
					filters={filters}
				/>

				{/* Announcements Table */}
				{isLoading ? (
					<div className="flex items-center justify-center h-64">
						<div className="text-center">
							<Icon
								name="Loader2"
								size={48}
								className="animate-spin text-primary mx-auto mb-4"
							/>
							<p className="text-muted-foreground">Loading announcements...</p>
						</div>
					</div>
				) : (
					<>
						<AnnouncementTable
							announcements={announcements}
							onEditAnnouncement={handleEditAnnouncement}
							onDeleteAnnouncement={handleDeleteAnnouncement}
						/>

						{/* Empty State */}
						{announcements.length === 0 && (
							<div className="bg-card rounded-lg border border-border p-6 sm:p-12 text-center">
								<Icon
									name="Megaphone"
									size={64}
									className="text-muted-foreground mx-auto mb-4"
								/>
								<h3 className="text-lg font-medium text-foreground mb-2">
									No announcements found
								</h3>
								<p className="text-muted-foreground mb-6">
									No announcements match your current filters. Try adjusting
									your search criteria or create a new announcement.
								</p>
								<Button
									onClick={() => setIsCreateModalOpen(true)}
									iconName="Plus"
									iconPosition="left"
								>
									Create Your First Announcement
								</Button>
							</div>
						)}
					</>
				)}

				{/* Pagination */}
				{announcementsData &&
					(announcementsData.next || announcementsData.previous) && (
						<div className="mt-6 flex items-center justify-between">
							<Button
								variant="outline"
								onClick={() => setPage((p) => Math.max(1, p - 1))}
								disabled={!announcementsData.previous}
								iconName="ChevronLeft"
								iconPosition="left"
							>
								Previous
							</Button>
							<span className="text-sm text-muted-foreground">
								Page {page} of {Math.ceil(announcementsData.count / 20)}
							</span>
							<Button
								variant="outline"
								onClick={() => setPage((p) => p + 1)}
								disabled={!announcementsData.next}
								iconName="ChevronRight"
								iconPosition="right"
							>
								Next
							</Button>
						</div>
					)}
				{/* Create Announcement Modal */}
				<CreateAnnouncementModal
					isOpen={isCreateModalOpen}
					onClose={() => setIsCreateModalOpen(false)}
					onSuccess={handleCreateSuccess}
					isLoading={isCreating}
				/>

				{/* Edit Announcement Modal */}
				<EditAnnouncementModal
					isOpen={isEditModalOpen}
					onClose={() => {
						setIsEditModalOpen(false);
						setSelectedAnnouncement(null);
					}}
					onSuccess={handleUpdateSuccess}
					announcement={selectedAnnouncement}
					isLoading={isUpdating}
				/>
			</div>
		</div>
	);
};

export default AnnouncementManagement;
